import React, { memo, useMemo, useState } from 'react';
import moment from 'moment';
import { TableBudget } from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { CalculateIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import {
  differencesToHighlight,
  getQueryStringValue,
  resolveActualDateLabel,
  resolveBudgetSections,
} from 'containers/RentForecast/utils';
import AutoCalculateModal from 'containers/RentForecast/RDBudgetModal/autoCalculateModal';
import type {
  BudgetType,
  Part2ColumnKey,
  SectionConfiguration,
} from 'containers/RentForecast/RDBudgetModal/types';
import useIntl from 'hooks/useIntl';
import { toastr } from 'react-redux-toastr';
import rentForecastMessages from 'containers/RentForecast/messages';
import { parseMonetaryValue } from 'utils/parsers';
import { FormattedMessage } from 'react-intl';
import {
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import messages from 'containers/RentForecast/messages';

const PAGE_INDEX = '1';

function calculateProposed(lineNumber, current, actual, numUnits) {
  switch (lineNumber) {
    case 1:
      return 60 * numUnits * 12;
    case 2:
      return actual * 2 * 1.05;
    case 3:
      return numUnits * 160;
    case 4:
      return actual * 2 * 1.05;
    case 7:
      return 15_000;
    case 8:
      return Math.max(current, actual * 2 * 1.05);
    case 10:
      return Math.max(actual * 2, current);
    case 12:
      return actual * 2 * 1.05;
    case 13:
      return actual * 2 * 1.05;
    case 14:
      return actual * 2 * 1.05;
    case 15:
      return actual * 2 * 1.05;
    case 16:
      return actual * 2 * 1.05;
    case 19:
      return Math.max(current * 1.04, actual * 2 * 1.09);
    case 21:
      return Math.max(actual + 100, current + 100);
    case 22:
      return Math.max(current, actual);
    case 23:
      return Math.max(current, actual);
    case 24:
      return Math.max(current, actual);
    case 25:
      return Math.max(current + 5 * numUnits, actual * 2 * 1.05 + 5 * numUnits);
    case 26:
      return 1000;
    case 27:
      return 600;
    case 28:
      return Math.max(current, 3_000);
    case 29:
      return 175 * numUnits;
    case 30:
      return Math.max(current * 1.04, actual * 2 * 1.08);
    case 31:
      return 40 * numUnits;
    case 32:
      return Math.max(current * 1.05, actual * 2 * 1.09);
    case 34:
      return Math.max(current * 1.05, actual * 1.09);
    case 36:
      return Math.max(current, actual);
    case 37:
      return Math.max(current * 1.05, actual * 1.09);
    default:
      return current;
  }
}

export const data: SectionConfiguration<Part2ColumnKey>[] = [
  {
    header: 'Operating and Maintenance',
    rows: [
      {
        label: '1. Maintenance and Repairs Payroll',
        labelTooltip: (
          <Stack direction="column" spacing={1} padding={1}>
            <Typography variant="titleSmall" fontWeight="bold">
              <FormattedMessage
                {...messages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage {...rentForecastMessages.part2line1} />
            </Typography>
          </Stack>
        ),
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: '2. Maintenance and Repairs Supply',
        labelTooltip: (
          <Stack direction="column" spacing={1} padding={1}>
            <Typography variant="titleSmall" fontWeight="bold">
              <FormattedMessage
                {...messages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage {...rentForecastMessages.part2line2} />
            </Typography>
          </Stack>
        ),
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: '3. Maintenance and Repairs Contract',
        labelTooltip: (
          <Stack direction="column" spacing={1} padding={1}>
            <Typography variant="titleSmall" fontWeight="bold">
              <FormattedMessage
                {...messages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage {...rentForecastMessages.part2line3} />
            </Typography>
          </Stack>
        ),
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: '4. Painting',
        labelTooltip: (
          <Stack direction="column" spacing={1} padding={1}>
            <Typography variant="titleSmall" fontWeight="bold">
              <FormattedMessage
                {...messages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage {...rentForecastMessages.part2line4} />
            </Typography>
          </Stack>
        ),
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: '5. Snow Removal',
        skipKeys: ['variancePercentage'],
      },
      {
        label: '6. Elevator Maintenance/Contract',
        skipKeys: ['variancePercentage'],
      },
      {
        label: '7. Grounds',
        labelTooltip: (
          <Stack direction="column" spacing={1} padding={1}>
            <Typography variant="titleSmall" fontWeight="bold">
              <FormattedMessage
                {...messages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage {...rentForecastMessages.part2line7} />
            </Typography>
          </Stack>
        ),
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: '8. Services',
        labelTooltip: (
          <Stack direction="column" spacing={1} padding={1}>
            <Typography variant="titleSmall" fontWeight="bold">
              <FormattedMessage
                {...messages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage {...rentForecastMessages.part2line8} />
            </Typography>
          </Stack>
        ),
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: '9. Annual Capital Budget',
        labelSuffix: '(From Part V - Operating)',
        skipKeys: ['variancePercentage'],
        disabledWhen: {
          actual: ['normal'],
          proposedBudget: ['normal'],
        },
      },
      {
        label: '10. Other Operating Expenses',
        labelSuffix: '(Itemize)',
        labelTooltip: (
          <Stack direction="column" spacing={1} padding={1}>
            <Typography variant="titleSmall" fontWeight="bold">
              <FormattedMessage
                {...messages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage {...rentForecastMessages.part2line10} />
            </Typography>
          </Stack>
        ),
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: '11. Sub-Total Maint. and Operating',
        labelSuffix: '(1 thru 10)',
        labelTooltip: (
          <FormattedMessage {...rentForecastMessages.part2VarianceTooltip} />
        ),
        autoCalculate: true,
        disabledWhen: {
          // Total should be disabled regardless of budget type, approved budget will disable them regardless
          currentBudget: ['custom'],
          actual: ['normal', 'custom'],
          proposedBudget: ['normal', 'custom'],
        },
      },
    ],
  },
  {
    header: 'Utilities',
    headerSuffix: '(*If master metered check box on front)',
    rows: [
      {
        label: '12. Electricity *',
        labelTooltip: (
          <Stack direction="column" spacing={1} padding={1}>
            <Typography variant="titleSmall" fontWeight="bold">
              <FormattedMessage
                {...messages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage {...rentForecastMessages.part2line12} />
            </Typography>
          </Stack>
        ),
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: '13. Water *',
        labelTooltip: (
          <Stack direction="column" spacing={1} padding={1}>
            <Typography variant="titleSmall" fontWeight="bold">
              <FormattedMessage
                {...messages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage {...rentForecastMessages.part2line13} />
            </Typography>
          </Stack>
        ),
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: '14. Sewer *',
        labelTooltip: (
          <Stack direction="column" spacing={1} padding={1}>
            <Typography variant="titleSmall" fontWeight="bold">
              <FormattedMessage
                {...messages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage {...rentForecastMessages.part2line14} />
            </Typography>
          </Stack>
        ),
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: '15. Fuel (Oil, Coal, Gas)',
        labelTooltip: (
          <Stack direction="column" spacing={1} padding={1}>
            <Typography variant="titleSmall" fontWeight="bold">
              <FormattedMessage
                {...messages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage {...rentForecastMessages.part2line15} />
            </Typography>
          </Stack>
        ),
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: '16. Garbage and Trash Removal',
        labelTooltip: (
          <Stack direction="column" spacing={1} padding={1}>
            <Typography variant="titleSmall" fontWeight="bold">
              <FormattedMessage
                {...messages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage {...rentForecastMessages.part2line16} />
            </Typography>
          </Stack>
        ),
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: '17. Other Utilities',
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: '18. Sub-Total Utilities',
        labelSuffix: '(12 thru 17)',
        labelTooltip: (
          <FormattedMessage {...rentForecastMessages.part2VarianceTooltip} />
        ),
        autoCalculate: true,
        disabledWhen: {
          // Total should be disabled regardless of budget type, approved budget will disable them regardless
          currentBudget: ['custom'],
          actual: ['normal', 'custom'],
          proposedBudget: ['normal', 'custom'],
        },
      },
    ],
  },
  {
    header: 'Administrative',
    rows: [
      {
        label: '19. Site Management Payroll',
        labelTooltip: (
          <Stack direction="column" spacing={1} padding={1}>
            <Typography variant="titleSmall" fontWeight="bold">
              <FormattedMessage
                {...messages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage {...rentForecastMessages.part2line19} />
            </Typography>
          </Stack>
        ),
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: '20. Management Fee',
        skipKeys: ['variancePercentage'],
        disabledWhen: {
          proposedBudget: ['normal'],
          comments: ['normal'],
        },
        dynamicTooltip: ({ parameters }) => {
          return parameters.managementFeeEquation ? (
            <Stack direction="column" padding={1} spacing={1}>
              <Typography variant="titleSmall" fontWeight="bold">
                <FormattedMessage
                  {...messages.rdBudgetFormTooltipProposedValue}
                />
              </Typography>
              <Typography variant="bodySmall">
                {parameters.managementFeeEquation}
              </Typography>
            </Stack>
          ) : (
            <FormattedMessage
              {...messages.rdBudgetrdBudgetFormTooltipManagementFeesNotConfigured}
            />
          );
        },
      },
      {
        label: '21. Project Auditing Expense',
        labelTooltip: (
          <Stack direction="column" spacing={1} padding={1}>
            <Typography variant="titleSmall" fontWeight="bold">
              <FormattedMessage
                {...messages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage {...rentForecastMessages.part2line21} />
            </Typography>
          </Stack>
        ),
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: '22. Project Bookkeeping/Accounting',
        labelTooltip: (
          <Stack direction="column" spacing={1} padding={1}>
            <Typography variant="titleSmall" fontWeight="bold">
              <FormattedMessage
                {...messages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage {...rentForecastMessages.part2line22} />
            </Typography>
          </Stack>
        ),
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: '23. Legal Expenses',
        labelTooltip: (
          <Stack direction="column" spacing={1} padding={1}>
            <Typography variant="titleSmall" fontWeight="bold">
              <FormattedMessage
                {...messages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage {...rentForecastMessages.part2line23} />
            </Typography>
          </Stack>
        ),
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: '24. Advertising',
        labelTooltip: (
          <Stack direction="column" spacing={1} padding={1}>
            <Typography variant="titleSmall" fontWeight="bold">
              <FormattedMessage
                {...messages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage {...rentForecastMessages.part2line24} />
            </Typography>
          </Stack>
        ),
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: '25. Telephone and Answering Service',
        labelTooltip: (
          <Stack direction="column" spacing={1} padding={1}>
            <Typography variant="titleSmall" fontWeight="bold">
              <FormattedMessage
                {...messages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage {...rentForecastMessages.part2line25} />
            </Typography>
          </Stack>
        ),
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: '26. Office Supplies',
        labelTooltip: (
          <Stack direction="column" spacing={1} padding={1}>
            <Typography variant="titleSmall" fontWeight="bold">
              <FormattedMessage
                {...messages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage {...rentForecastMessages.part2line26} />
            </Typography>
          </Stack>
        ),
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: '27. Office Funiture and Equipment',
        labelTooltip: (
          <Stack direction="column" spacing={1} padding={1}>
            <Typography variant="titleSmall" fontWeight="bold">
              <FormattedMessage
                {...messages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage {...rentForecastMessages.part2line27} />
            </Typography>
          </Stack>
        ),
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: '28. Training Expense',
        labelTooltip: (
          <Stack direction="column" spacing={1} padding={1}>
            <Typography variant="titleSmall" fontWeight="bold">
              <FormattedMessage
                {...messages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage {...rentForecastMessages.part2line28} />
            </Typography>
          </Stack>
        ),
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: '29. Health Ins. and Other Emp. Benefits',
        labelTooltip: (
          <Stack direction="column" spacing={1} padding={1}>
            <Typography variant="titleSmall" fontWeight="bold">
              <FormattedMessage
                {...messages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage {...rentForecastMessages.part2line29} />
            </Typography>
          </Stack>
        ),
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: '30. Payroll Taxes',
        labelTooltip: (
          <Stack direction="column" spacing={1} padding={1}>
            <Typography variant="titleSmall" fontWeight="bold">
              <FormattedMessage
                {...messages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage {...rentForecastMessages.part2line30} />
            </Typography>
          </Stack>
        ),
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: "31. Worker's Compensation",
        labelTooltip: (
          <Stack direction="column" spacing={1} padding={1}>
            <Typography variant="titleSmall" fontWeight="bold">
              <FormattedMessage
                {...messages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage {...rentForecastMessages.part2line31} />
            </Typography>
          </Stack>
        ),
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: '32. Other Administrative Expenses',
        labelSuffix: '(Itemize)',
        labelTooltip: (
          <Stack direction="column" spacing={1} padding={1}>
            <Typography variant="titleSmall" fontWeight="bold">
              <FormattedMessage
                {...messages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage {...rentForecastMessages.part2line32} />
            </Typography>
          </Stack>
        ),
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: '33. Sub-Total Administrative',
        labelSuffix: '(19 thru 32)',
        labelTooltip: (
          <FormattedMessage {...rentForecastMessages.part2VarianceTooltip} />
        ),
        autoCalculate: true,
        disabledWhen: {
          currentBudget: ['custom'],
          actual: ['normal', 'custom'],
          proposedBudget: ['normal', 'custom'],
        },
      },
    ],
  },
  {
    header: 'Taxes and Insurance',
    rows: [
      {
        label: '34. Real Estate Taxes',
        labelTooltip: (
          <Stack direction="column" spacing={1} padding={1}>
            <Typography variant="titleSmall" fontWeight="bold">
              <FormattedMessage
                {...messages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage {...rentForecastMessages.part2line34} />
            </Typography>
          </Stack>
        ),
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: '35. Special Assessments',
        skipKeys: ['variancePercentage'],
      },
      {
        label: '36. Other Taxes, Licenses, and Permits',
        labelTooltip: (
          <Stack direction="column" spacing={1} padding={1}>
            <Typography variant="titleSmall" fontWeight="bold">
              <FormattedMessage
                {...messages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage {...rentForecastMessages.part2line36} />
            </Typography>
          </Stack>
        ),
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: '37. Property and Liability Insurance',
        labelTooltip: (
          <Stack direction="column" spacing={1} padding={1}>
            <Typography variant="titleSmall" fontWeight="bold">
              <FormattedMessage
                {...messages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage {...rentForecastMessages.part2line37} />
            </Typography>
          </Stack>
        ),
        skipKeys: ['variancePercentage'],
        autoCalculate: true,
      },
      {
        label: '38. Fidelity Coverage Insurance',
        skipKeys: ['variancePercentage'],
      },
      {
        label: '39. Other Insurance',
        skipKeys: ['variancePercentage'],
      },
      {
        label: '40. Sub-Total Taxes and Insurance',
        labelSuffix: '(34 thru 39)',
        labelTooltip: (
          <FormattedMessage {...rentForecastMessages.part2VarianceTooltip} />
        ),
        autoCalculate: true,
        disabledWhen: {
          currentBudget: ['custom'],
          actual: ['normal', 'custom'],
          proposedBudget: ['normal', 'custom'],
        },
      },
      {
        label: '41. Total O&M Expenses',
        labelSuffix: '(11+18+33+40)',
        skipKeys: ['variancePercentage'],
        wizardDisabled: true,
        disabledWhen: {
          currentBudget: ['custom'],
          actual: ['normal', 'custom'],
          proposedBudget: ['normal', 'custom'],
        },
      },
    ],
  },
];

export const generateHeaders = (
  year,
  actualDateRange,
  differenceCount,
  enableAutoCalculate,
  budgetType: BudgetType,
  setOpenAutoCalculate,
  rentForecastCreatedDate: string,
  disableIntacctActuals: boolean = false,
) => [
  {
    label: 'Modified After Intacct sync',
    subLabel: `${differenceCount} total items across Parts I, II, III, and V`,
    legend: { backgroundColor: 'warning.lighter' },
    flexSize: 2,
    textAlign: 'left',
    sticky: true,
    hidden: budgetType === 'custom',
    hideHeader: budgetType === 'custom',
  },
  {
    label: `${budgetType === 'approved' ? 'Approved' : 'Current'} Budget`,
    subLabel: `01/01/${year} - 12/31/${year}`,
    key: 'currentBudget',
    flexSize: 1,
    placeholder: '0.00',
    type: 'number-currency',
    disabled:
      budgetType === 'disabled' ||
      budgetType === 'custom-disabled' ||
      budgetType === 'normal' ||
      budgetType === 'approved',
  },
  {
    label: 'Actual',
    subLabel: resolveActualDateLabel(year, actualDateRange),
    key: 'actual',
    flexSize: 1,
    emptyValue: 0,
    type: 'number-currency',
    disabled:
      budgetType === 'disabled' ||
      budgetType === 'custom-disabled' ||
      budgetType === 'approved',
  },
  {
    label: 'Proposed Budget',
    subLabel: `01/01/${parseInt(year) + 1} - 12/31/${parseInt(year) + 1}`,
    key: 'proposedBudget',
    flexSize: 1,
    type: 'number-currency',
    emptyValue: 0,
    disabled:
      budgetType === 'disabled' ||
      budgetType === 'custom-disabled' ||
      budgetType === 'approved',
    ...(budgetType === 'normal'
      ? {
          action: {
            label: 'Auto-Calculate',
            startIcon: <CalculateIcon />,
            onClick: () => setOpenAutoCalculate(true),
          },
        }
      : {}),
  },
  ...(budgetType?.startsWith('custom')
    ? [
        {
          label: 'YTD Actuals',
          subLabel: resolveActualDateLabel(year, {
            from: moment(rentForecastCreatedDate).startOf('year'),
            to: moment(rentForecastCreatedDate),
          }),
          key: 'intacctActual',
          flexSize: 1,
          emptyValue: 0,
          type: 'number-currency',
          disabled: budgetType === 'custom-disabled' || disableIntacctActuals,
        },
      ]
    : []),
  {
    key: 'variancePercentage',
    label: 'Variance %',
    flexSize: 1,
    type: 'number-percentage',
    emptyValue: 0,
    skipFields: true,
    disabled: true,
    alignContent: 'right',
  },
  {
    label: 'Comments',
    key: 'comments',
    textAlign: 'left',
    flexSize: 1,
    placeholder: 'Comment',
    type: 'text',
    disabled:
      budgetType === 'disabled' ||
      budgetType === 'custom-disabled' ||
      budgetType === 'approved',
  },
];

const Part2 = ({
  useFormReturn,
  year,
  intacctDifferences,
  enableAutoCalculate,
  actualDateRange,
  rentForecast,
  budgetType,
  totalUnits,
  managementFeeData,
}) => {
  const intl = useIntl();
  const [highlightFields, differenceCount] = useMemo(
    () => differencesToHighlight(intacctDifferences),
    [intacctDifferences],
  );
  const [openAutoCalculate, setOpenAutoCalculate] = useState(false);

  const sections = useMemo(
    () =>
      resolveBudgetSections(data, budgetType, {
        parameters: {
          managementFeeEquation: managementFeeData?.updatedEquation,
        },
      }),
    [rentForecast, managementFeeData?.updatedEquation],
  );

  const headers = generateHeaders(
    year,
    actualDateRange,
    differenceCount,
    enableAutoCalculate,
    budgetType,
    setOpenAutoCalculate,
    rentForecast?.createdAt,
    !rentForecast?.parameters?.intacctYTDActuals,
  );

  const handleAutoCalculateSubmit = () => {
    data.forEach((section, sectionIndex) => {
      section.rows.forEach((row, rowIndex) => {
        if (!row.autoCalculate) return;

        const keyPrefix = `${PAGE_INDEX}-${sectionIndex}-${rowIndex}`;
        const n = calculateProposed(
          Number(row.label.split('.')[0]),
          parseMonetaryValue(
            useFormReturn.getValues(`${keyPrefix}-currentBudget`),
          ),
          parseMonetaryValue(useFormReturn.getValues(`${keyPrefix}-actual`)),
          totalUnits,
        );

        useFormReturn.setValue(`${keyPrefix}-proposedBudget`, n, {
          shouldValidate: true,
          shouldDirty: true,
        });
      });
    });

    setOpenAutoCalculate(false);

    toastr.success(
      intl.formatMessage(rentForecastMessages.success),
      intl.formatMessage(rentForecastMessages.autoCalculationSuccess),
    );
  };

  const renderAll = getQueryStringValue('fullRenderTable') === 'true';

  return (
    <>
      <AutoCalculateModal
        open={openAutoCalculate}
        closeModal={() => setOpenAutoCalculate(false)}
        onSubmit={handleAutoCalculateSubmit}
      />
      <TableBudget
        fieldNamePrefix={PAGE_INDEX}
        columns={headers}
        sections={sections}
        useFormReturn={useFormReturn}
        tableBodySize={{ height: 800, width: 1488 }}
        highlightFields={highlightFields}
        renderAll={renderAll}
      />
    </>
  );
};

export default memo(Part2);
